import type { TablePlayerIdentity } from "@bing/shared";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Avatar } from "../components/Avatar";
import { ChatComposer, ChatConnectionNotice, ChatMessageList } from "../components/ChatPanel";
import { GameIcon } from "../components/GameIcon";
import { displayName, profileDisplayName } from "../components/profile-ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ReportSheet } from "../components/ReportSheet";
import { IconButton, OfficialBadge, Pill, Sheet } from "../components/social-ui";
import { faDigits } from "../i18n/digits";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import type { FriendView } from "../api/client";
import { createChatStore } from "./chat-store";
import type { LobbyPlayer } from "./online-connection";
import { KEEPS_ITS_ORDER, nameBoxDir } from "./name-room";
import { createColyseusTableChatConnector } from "./table-chat";

/**
 * The in-table social surface for an ONLINE match, mounted by the online shell
 * only (solo games have no table chat):
 *
 *  - a round toggle pinned in the tables' HEADER BAND (beside every table's
 *    exit button — the one strip all five tables keep clear) at `z-[55]`, ABOVE
 *    every table layer (headers z-40/50, bottom hand panel z-30, the turn pill
 *    z-45 and the paused scrim z-40) so it is always reachable — the old bottom
 *    toggle sat at z-26 UNDER the tables' z-30 hand panel ("stuck under the
 *    cards"). An unread dot shows for messages that land while the sheet is
 *    closed (session-local only).
 *  - the shared bottom {@link Sheet} (z-[70], backdrop fade + slide-up) with two
 *    tabs: «گفتگو» — the shared chat UI (`ChatMessageList` with sender captions
 *    + `ChatComposer` with the emoji grid and voice notes) bound to the
 *    table-chat connector, correlated to table-mates by this table's Colyseus
 *    `roomId`; and «بازیکن‌ها» — the live roster with a real friend-state action
 *    per human (see {@link TablePlayersPanel}).
 *
 * Z decision (documented): the sheet uses the shared Sheet's z-[70], one layer
 * ABOVE Hokm's fixed win banner (z-[60]) and the (z-auto) WinOverlay — when a
 * match ends while chatting, the sheet deliberately wins until closed; the
 * closed toggle (z-[55]) stays below both, so the win moment itself is never
 * covered by chrome.
 */
export function TableChat({
  wsUrl,
  tableRoomId,
  initData,
  selfId,
  title,
  players,
  selfSeat,
  chatAllowed = true,
  isHost = false,
  onKick,
  myTurn = false,
}: {
  wsUrl: string;
  tableRoomId: string;
  initData: string | null;
  /** Our Telegram id — chat messages from it render as "mine" (the chat room
   * keys senders on the Telegram id from initData). */
  selfId: string;
  title: string;
  /** The lobby roster (server-broadcast). Humans may carry their leak-free
   * public identity (`user`) — the players tab's avatar/name/friend-action. */
  players?: readonly LobbyPlayer[];
  /** Our seat — marks the own roster row even if identity didn't resolve. */
  selfSeat?: number | null;
  /** The owner's «گفتگو» table policy. `false` ⇒ the chat tab/composer are hidden
   * (this surface becomes a players-only roster); the friend actions stay. */
  chatAllowed?: boolean;
  /** Are WE this private table's host? Gates the «اخراج» control on every other roster row.
   *  Defaults false, so a surface that forgets to pass it offers nothing — the safe direction. */
  isHost?: boolean;
  /** Remove the player at `seat` from the table («اخراج»). Absent ⇒ the control is not offered
   *  at all, which is what a PUBLIC table (no host) and every pre-host state get. */
  onKick?: (seat: number) => void;
  /** It is OUR turn right now — the report sheet says so and offers the felt in one tap. */
  myTurn?: boolean;
}): ReactNode {
  const api = useSession((s) => s.api);
  const store = useMemo(
    () =>
      createChatStore(
        createColyseusTableChatConnector({
          wsUrl,
          tableRoomId,
          getInitData: () => initData,
        }),
        { selfId }, // a live table line chimes; our own echo never does
      ),
    [wsUrl, tableRoomId, initData, selfId],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const messages = useStore(store, (s) => s.messages);
  const send = useStore(store, (s) => s.send);
  const sendVoice = useStore(store, (s) => s.sendVoice);
  const retry = useStore(store, (s) => s.retry);
  // ⚠️ THREADED, not inferred: `failed` alone cannot tell «the network went» from «the table is
  // gone», and only the second one makes the retry button a lie. See `ChatState.canRetry`.
  const canRetry = useStore(store, (s) => s.canRetry);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"chat" | "players">(chatAllowed ? "chat" : "players");
  const [seen, setSeen] = useState(0);
  // Messages count as seen only while the CHAT tab is actually visible.
  const chatVisible = open && tab === "chat" && chatAllowed;
  useEffect(() => {
    if (chatVisible) setSeen(messages.length);
    // A reconnect REPLACES table chat wholesale (no server history → empty-fresh):
    // clamp the watermark so the unread dot tracks post-rejoin messages instead of
    // comparing against a pre-wipe count it can never reach again.
    else setSeen((n) => Math.min(n, messages.length));
  }, [chatVisible, messages.length]);
  // No unread affordance when the owner turned chat off (there is no chat to miss).
  const unread = chatAllowed && messages.length > seen;

  const openSheet = (): void => {
    setTab(chatAllowed ? "chat" : "players");
    setOpen(true);
  };

  /**
   * ⚠️ THE REPORT DRAFTS LIVE HERE, NOT IN THE ROSTER — A REPORT MUST NEVER BE A TRAP (2026-10-03).
   * A player sworn at on a حکم table learned inside the sheet that توهین needs a screenshot, found
   * no way back, and left the table. The roster unmounts with the chat sheet and the report sheet
   * used to unmount on close, so every way back started the report over — and an upload in flight
   * resolved into a dead component. Now each player with a draft keeps ONE mounted (possibly
   * hidden) sheet for the table's whole life; `gen` restarts a filed one.
   */
  const [reports, setReports] = useState<readonly ReportDraft[]>([]);
  const [reportShown, setReportShown] = useState<string | null>(null);
  /** `gen` pins a patch to ONE draft: a filing that lands after its draft was dropped and the
   *  player started again must not mark the NEW draft done (review ②, 2026-10-03). */
  const patchReport = (userId: string, patch: Partial<ReportDraft>, gen?: number): void =>
    setReports((list) =>
      list.map((d) =>
        d.userId === userId && (gen === undefined || d.gen === gen) ? { ...d, ...patch } : d,
      ),
    );
  const dropReport = (userId: string): void => {
    setReports((list) => list.filter((d) => d.userId !== userId));
    setReportShown((shown) => (shown === userId ? null : shown));
  };
  /** Table-wide, never per player: after «لغو گزارش» there is no old draft left to count from,
   *  and a restart that reused its number would let the dropped draft's late filing claim it. */
  const reportGen = useRef(0);
  const startReport = (userId: string, name: string): void => {
    const gen = (reportGen.current += 1);
    setReports((list) => {
      const had = list.find((d) => d.userId === userId);
      if (had && !had.done) return list;
      const fresh: ReportDraft = {
        userId,
        name,
        gen,
        filled: false,
        parked: false,
        done: false,
      };
      return [...list.filter((d) => d.userId !== userId), fresh];
    });
    setReportShown(userId);
  };
  /** Gone to the table on purpose (the screenshot, our turn): keep even an untouched draft —
   *  the PLAYER is already chosen, and that is part of what must not be chosen again. */
  const parkReport = (userId: string): void => {
    patchReport(userId, { parked: true });
    setReportShown(null);
  };
  const resumable = reports.filter((d) => !d.done && (d.filled || d.parked));

  return (
    <>
      {!open ? (
        <button
          type="button"
          aria-label={fa.chat.openChat}
          onClick={openSheet}
          className="fixed z-[55] flex h-10 w-10 cursor-pointer appearance-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/85 text-secondary shadow-md backdrop-blur-sm transition-transform active:scale-95"
          style={{
            // The tables' shared header band: every table pins its exit button at
            // the inline-start edge in this strip — we sit right beside it.
            top: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
            // Align to the CENTERED table column (the tables are max-w-[440px] mx-auto),
            // not the raw viewport — otherwise on any width > 440px (desktop / large
            // phones) this fixed toggle drifts away from the table's exit button. Sit at
            // the column's inline-start + 64px, right beside that exit.
            insetInlineStart: "calc(max(0px, (100vw - 440px) / 2) + 64px)",
          }}
        >
          <GameIcon type="chat" size={19} />
          {unread ? (
            <span
              aria-hidden="true"
              className="absolute rounded-full bg-error"
              style={{
                top: 5,
                insetInlineEnd: 5,
                width: 9,
                height: 9,
                border: "2px solid rgb(var(--c-surface-container))",
              }}
            />
          ) : null}
        </button>
      ) : (
        <Sheet
          label={chatAllowed ? fa.chat.tableChat : fa.chat.playersTab}
          onClose={() => setOpen(false)}
          flush
        >
          <div className="flex flex-col" style={{ height: "min(64vh, 480px)" }}>
            {/* Compact header: table-chat title (+ game) and the close X. */}
            <div className="flex items-center gap-2 px-4 pb-2">
              <span className="flex items-center text-secondary">
                <GameIcon type="chat" size={17} />
              </span>
              <p className="m-0 min-w-0 flex-1 truncate font-body-lg text-body-lg text-on-surface">
                {chatAllowed ? fa.chat.tableChat : fa.chat.playersTab}
                <span className="text-on-surface-variant">{` · ${title}`}</span>
              </p>
              <button
                type="button"
                aria-label={fa.chat.closeChat}
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border border-outline-variant bg-surface-container-high text-on-surface-variant"
              >
                <svg
                  viewBox="0 0 24 24"
                  width={15}
                  height={15}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            {/* A report the player stepped away from — the one-tap way back into it, here
                because this is where they land when they go for the screenshot. */}
            {resumable.map((d) => (
              <div
                key={d.userId}
                data-testid="report-resume"
                className="mx-4 mb-1 flex items-center gap-2 rounded-xl bg-secondary/15 px-3 py-2"
              >
                <p className="m-0 min-w-0 flex-1 font-body-md text-caption text-on-surface">
                  {fa.report.resumeLabel(d.name)}
                </p>
                <button
                  type="button"
                  onClick={() => setReportShown(d.userId)}
                  className="flex-none cursor-pointer appearance-none rounded-full border-0 bg-gold-deep px-3 py-1.5 font-body-md text-caption font-bold text-ink-on-gold transition-transform active:scale-95"
                >
                  {fa.report.resume}
                </button>
                <button
                  type="button"
                  aria-label={fa.report.discard}
                  onClick={() => dropReport(d.userId)}
                  className="flex h-7 w-7 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border-0 bg-surface-container-highest text-body-md text-on-surface-variant"
                >
                  ✕
                </button>
              </div>
            ))}

            {/* گفتگو | بازیکن‌ها — the Friends screen's segmented-tab pattern. Only when
                the owner allows chat; a chat-OFF table is a players-only roster (no tabs). */}
            {chatAllowed ? (
              <div className="mx-4 mb-1 flex gap-1 rounded-xl bg-surface-container-highest p-1">
                {(
                  [
                    { key: "chat", label: fa.chat.chatTab, dot: unread },
                    { key: "players", label: fa.chat.playersTab, dot: false },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    aria-label={t.label}
                    aria-pressed={tab === t.key}
                    onClick={() => setTab(t.key)}
                    className={`relative flex flex-1 appearance-none items-center justify-center gap-1.5 rounded-lg border-0 py-1.5 font-body-md text-body-md transition-colors ${
                      tab === t.key
                        ? "bg-gold-deep text-ink-on-gold shadow"
                        : "cursor-pointer bg-transparent text-on-surface-variant hover:text-on-surface"
                    }`}
                  >
                    <span>{t.label}</span>
                    {t.dot && tab !== t.key ? (
                      <span aria-hidden="true" className="h-2 w-2 rounded-full bg-error" />
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}

            {chatAllowed && tab === "chat" ? (
              <>
                <ChatMessageList
                  messages={messages}
                  selfId={selfId}
                  connecting={status === "connecting"}
                  showSenders
                />
                {/* Slim reconnect/failed strip — the sheet's own state; a chat drop
                    must never raise the full-screen game ConnectionOverlay. */}
                <ChatConnectionNotice status={status} onRetry={retry} canRetry={canRetry} />
                <div className="flex-none border-t border-outline-variant/30">
                  {/* Muted unless live — a send that can't be delivered is refused
                      by disabling, not queued or silently dropped. */}
                  <ChatComposer
                    onSend={send}
                    onSendVoice={(ref) => sendVoice(ref.voiceId, ref.durationMs)}
                    disabled={status !== "open"}
                  />
                </div>
              </>
            ) : (
              <TablePlayersPanel
                players={players}
                selfSeat={selfSeat ?? null}
                isHost={isHost}
                {...(onKick ? { onKick } : {})}
                onReport={startReport}
              />
            )}
          </div>
        </Sheet>
      )}
      {/* OUTSIDE the open/closed branch, so a draft outlives the chat sheet. Rendered after it,
          so a shown report paints over it at the same z-[70]. */}
      {reports.map((d) => (
        <ReportSheet
          key={`${d.userId}:${d.gen}`}
          name={d.name}
          hidden={reportShown !== d.userId}
          onSubmit={(input) =>
            api.reportUser(d.userId, input).then(() => patchReport(d.userId, { done: true }, d.gen))
          }
          onUpload={(blob, mime, onProgress) => api.uploadReportAttachment(blob, mime, onProgress)}
          // «بازگشت» — back to the roster it was opened from. A filed or untouched report has
          // nothing to keep; anything else stays for the «ادامهٔ گزارش» row.
          onClose={() =>
            d.done || !(d.filled || d.parked) ? dropReport(d.userId) : setReportShown(null)
          }
          onDraftChange={(filled) => patchReport(d.userId, { filled })}
          table={{
            myTurn,
            shotLandsOnChat: chatAllowed,
            // The evidence is the chat, so that is where the door lands; a chat-OFF table has
            // only the felt to show.
            onTakeScreenshot: () => {
              parkReport(d.userId);
              if (chatAllowed) {
                setTab("chat");
                setOpen(true);
              } else setOpen(false);
            },
            onBackToTable: () => {
              parkReport(d.userId);
              setOpen(false);
            },
          }}
        />
      ))}
    </>
  );
}

/** One player's report at this table — see `reports` in {@link TableChat}. */
interface ReportDraft {
  readonly userId: string;
  readonly name: string;
  /** Unique per started draft — the sheet's key, and the pin on every late patch. */
  readonly gen: number;
  /** Something is chosen — a reason, a line of text, a file. */
  readonly filled: boolean;
  /** Stepped away on purpose (the screenshot, our turn) — kept even if nothing is chosen. */
  readonly parked: boolean;
  readonly done: boolean;
}

/* ------------------------------------------------------------------ *
 * بازیکن‌ها — the live roster tab
 * ------------------------------------------------------------------ */

/** Preset avatars for seats without an identity (bot fills), keyed by seat —
 * the same deterministic mapping the win overlays use. */
const SEAT_AVATARS = ["lion", "wolf", "tiger", "eagle"] as const;

/** The relationship shown for a row: a store-list hit wins (the session store
 * maintains those on every mutation + poll); a mutation RESULT covers states no
 * list holds (e.g. blocked); absent everywhere → none. */
function relationOf(
  userId: string,
  lists: readonly (readonly FriendView[])[],
  overrides: ReadonlyMap<string, FriendView>,
): FriendView | undefined {
  for (const list of lists) {
    const hit = list.find((v) => v.userId === userId);
    if (hit) return hit;
  }
  return overrides.get(userId);
}

/** The per-state trailing action — the Friends screen's own pill/icon set:
 * none → افزودن دوست · outgoing → لغو درخواست · incoming → پذیرش/رد ·
 * accepted → دوستید (disabled) · blocked → آنبلاک. */
function RowAction({
  view,
  busy,
  on,
}: {
  view: FriendView | undefined;
  busy: boolean;
  on: {
    request: () => void;
    accept: () => void;
    reject: () => void;
    cancel: () => void;
    unblock: () => void;
  };
}): ReactNode {
  const status = view?.status ?? "none";
  if (status === "accepted") {
    return <Pill variant="muted" icon="group" label={fa.chat.alreadyFriends} />;
  }
  if (status === "blocked") {
    return (
      <Pill variant="outline" label={fa.friends.unblock} disabled={busy} onClick={on.unblock} />
    );
  }
  if (status === "pending") {
    return view?.direction === "incoming" ? (
      <>
        <IconButton
          tone="accept"
          icon="check"
          label={fa.friends.accept}
          disabled={busy}
          onClick={on.accept}
        />
        <IconButton
          tone="reject"
          icon="close"
          label={fa.friends.reject}
          disabled={busy}
          onClick={on.reject}
        />
      </>
    ) : (
      <Pill
        variant="outline"
        icon="cancel"
        label={fa.friends.cancel}
        disabled={busy}
        onClick={on.cancel}
      />
    );
  }
  return (
    <Pill
      variant="primary"
      icon="personAdd"
      label={fa.friends.add}
      disabled={busy}
      onClick={on.request}
    />
  );
}

/**
 * Every occupied seat, in seat order: shared Avatar (the real photo/preset from
 * the roster identity; a deterministic preset disc for bots), the shared
 * `displayNameOf`-priority name via `displayName`/`profileDisplayName` (bots are
 * labelled «بات ۱/۲/…» — never ids), and — for OTHER humans whose identity
 * resolved — the real friend-state action wired through the session store's
 * mutations. Rows re-derive their state from the store's friends/incoming/
 * outgoing lists (unknown → none) plus the last mutation's returned FriendView,
 * so a pill updates the moment the server answers.
 */
function TablePlayersPanel({
  players,
  selfSeat,
  isHost = false,
  onKick,
  onReport,
}: {
  players?: readonly LobbyPlayer[];
  selfSeat: number | null;
  isHost?: boolean;
  onKick?: (seat: number) => void;
  /** Open (or return to) the report on this player — the draft lives in {@link TableChat}. */
  onReport: (userId: string, name: string) => void;
}): ReactNode {
  const profile = useSession((s) => s.profile);
  const friends = useSession((s) => s.friends);
  const incoming = useSession((s) => s.incoming);
  const outgoing = useSession((s) => s.outgoing);
  const requestFriend = useSession((s) => s.requestFriend);
  const acceptFriend = useSession((s) => s.acceptFriend);
  const rejectFriend = useSession((s) => s.rejectFriend);
  const cancelFriend = useSession((s) => s.cancelFriend);
  const unblockUser = useSession((s) => s.unblockUser);

  /** The row the host tapped «اخراج» on, held while the confirmation is up (null = none).
   *  Removing somebody from a live game cannot be undone, so the tap only ever OPENS this. */
  const [kicking, setKicking] = useState<{ seat: number; name: string } | null>(null);
  const [overrides, setOverrides] = useState<ReadonlyMap<string, FriendView>>(new Map());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const mutate = (userId: string, fn: (id: string) => Promise<FriendView>): void => {
    setBusy((prev) => new Set(prev).add(userId));
    setError(null);
    void fn(userId)
      .then((view) => {
        // The store already reconciled its lists; keep the returned view too so
        // states no list holds (blocked / just-cancelled) still render truly.
        setOverrides((prev) => new Map(prev).set(userId, view));
      })
      .catch((e: unknown) => setError(faApiError(e)))
      .finally(() =>
        setBusy((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        }),
      );
  };

  const selfUserId = profile?.id ?? null;
  const lists = [friends, incoming, outgoing] as const;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 pt-2"
      style={{
        paddingBottom: "calc(16px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
      }}
    >
      {error ? (
        <p role="alert" className="m-0 text-center font-caption text-caption text-error">
          {error}
        </p>
      ) : null}
      {(players ?? []).map((p, i, all) => {
        const identity: TablePlayerIdentity | undefined = p.user;
        const isSelf = (identity && identity.userId === selfUserId) || p.seat === selfSeat;
        // Bots are numbered in seat order («بات ۱», «بات ۲», …) — never ids.
        const botNumber = all.slice(0, i + 1).filter((q) => q.isBot).length;
        const name = p.isBot
          ? `${fa.chat.botPlayer} ${faDigits(botNumber)}`
          : isSelf
            ? profileDisplayName(profile)
            : identity
              ? displayName(identity)
              : // Roster fallback: the server-resolved Telegram display name that
                // already labels lobby seats — a human name, never an id.
                p.name;
        const view =
          identity && !isSelf ? relationOf(identity.userId, lists, overrides) : undefined;
        return (
          <div
            key={p.seat}
            className="flex items-center justify-between gap-2 rounded-xl border border-surface-container-highest bg-surface-container-low p-2.5"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar
                avatarId={identity?.avatarId ?? SEAT_AVATARS[p.seat % SEAT_AVATARS.length] ?? "fox"}
                avatarPhotoId={identity?.avatarPhotoId ?? null}
                size={40}
                ringClass="border-gold-deep/60 shadow-sm"
                ringWidth={1}
              />
              <div className="flex min-w-0 items-center gap-1">
                {/* A long Latin name keeps its beginning, and only its cut changes: `nameBoxDir`. */}
                <p
                  dir={nameBoxDir(name)}
                  className="m-0 min-w-0 truncate font-body-lg text-body-lg text-on-surface"
                  style={{ textAlign: "right" }}
                >
                  <span style={KEEPS_ITS_ORDER}>
                    {name}
                    {isSelf ? (
                      <span className="text-on-surface-variant">{` ${fa.chat.youMarker}`}</span>
                    ) : null}
                  </span>
                </p>
                {identity?.isOfficial ? <OfficialBadge size={15} /> : null}
              </div>
            </div>
            {/* ⚠️ THIS GROUP WRAPS, AND THAT IS A MEASUREMENT, NOT A STYLE. A host's row on a
                private table now carries THREE controls — اخراج · گزارش · افزودن دوست — and at
                320 px they are 307 px of content in a 288 px row. It used to be `shrink-0`, which
                cannot overflow-scroll (no `overflow-x` anywhere above it) and so simply SPILLED.
                Measured with `report-probes/probe-roster-door.cjs`: 307 → fits, at all four
                widths, with the third pill dropping to its own end-aligned line at 320 only. */}
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
              {/* «اخراج» — the HOST only, on any OTHER human's chair. A bot chair has nobody to
                  remove, and the host cannot remove themselves (they have «خروج» for that, which
                  closes the table). Offered without an `identity`: the roster's own name is
                  enough to say who, and a table-mate whose profile did not resolve is exactly the
                  sort the host may most want gone. */}
              {isHost && onKick && !isSelf && !p.isBot ? (
                // The SHARED row pill, not a bespoke button — every other action on this row is
                // one, and a second look-alike is the thing `## Conventions` names. It reads
                // `outline` rather than shouting: the tap only OPENS a confirmation, and the
                // destructive styling belongs on that dialog's confirm, where the decision is.
                <Pill
                  variant="outline"
                  icon="remove"
                  label={fa.chat.kick}
                  onClick={() => setKicking({ seat: p.seat, name })}
                />
              ) : null}
              {/* «گزارش» — the door that was missing. Every block and every report on this
                  platform was filed from somewhere else, because the roster offered neither: a
                  player abused HERE had to remember the name, leave, and find them in search.
                  Needs a resolved `identity`, since the report is filed against a user id — the
                  one control on this row that cannot fall back to the roster's name. */}
              {identity && !isSelf && !p.isBot ? (
                <Pill
                  variant="outline"
                  icon="flag"
                  label={fa.report.action}
                  // The id AND the name: the sheet must say WHO it is about — reporting the
                  // wrong person off a four-seat roster is what its heading prevents.
                  onClick={() => onReport(identity.userId, name)}
                />
              ) : null}
              {identity && !isSelf && !p.isBot ? (
                <RowAction
                  view={view}
                  busy={busy.has(identity.userId)}
                  on={{
                    request: () => mutate(identity.userId, requestFriend),
                    accept: () => mutate(identity.userId, acceptFriend),
                    reject: () => mutate(identity.userId, rejectFriend),
                    cancel: () => mutate(identity.userId, cancelFriend),
                    unblock: () => mutate(identity.userId, unblockUser),
                  }}
                />
              ) : null}
            </div>
          </div>
        );
      })}
      {kicking && onKick ? (
        <ConfirmDialog
          title={fa.chat.kickConfirmTitle}
          body={fa.chat.kickConfirmBody(kicking.name)}
          confirmLabel={fa.chat.kickConfirm}
          cancelLabel={fa.chat.kickCancel}
          onConfirm={() => {
            onKick(kicking.seat);
            setKicking(null);
          }}
          onCancel={() => setKicking(null)}
        />
      ) : null}
    </div>
  );
}
